import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, AlertTriangle, Users, Wrench, Clock, CheckCircle2, ArrowRight } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    devices: {
      created_last_7_days: 0,
      created_last_7_days_products: [],
      blocked_last_7_days: 0,
      lost_last_7_days: 0,
      retired_last_7_days: 0
    },
    orders: {
      waiting_for_sns: 0,
      waiting_for_sns_products: [],
      completed_last_7_days: 0,
      completed_last_7_days_products: []
    },
    repairs: {
      waiting_for_repair: 0,
      waiting_for_repair_products: [],
      work_in_progress: 0,
      work_in_progress_products: [],
      ready_for_shipping: 0,
      ready_for_shipping_products: [],
      repaired: 0,
      repaired_products: [],
      created_last_7_days: 0,
      completed_last_7_days: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setError('');
      setLoading(true);
      
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to fetch dashboard statistics');
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, iconColor = 'bg-blue-500', onClick }) => {
    const cardContent = (
      <div className={`bg-white overflow-hidden shadow rounded-lg ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 ${iconColor} rounded-md flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
                <dd className="text-lg font-medium text-gray-900">{value}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );

    if (onClick) {
      return (
        <div onClick={onClick}>
          {cardContent}
        </div>
      );
    }

    return cardContent;
  };

  // Helper function to get border color based on status
  const getStatusBorderColor = (status) => {
    const colorMap = {
      // Repairs
      waiting_for_repair: 'border-yellow-500 hover:border-yellow-600',
      work_in_progress: 'border-blue-500 hover:border-blue-600',
      repaired: 'border-green-500 hover:border-green-600',
      ready_for_shipping: 'border-purple-500 hover:border-purple-600',
      // Orders
      pending: 'border-yellow-500 hover:border-yellow-600',
    };
    
    return colorMap[status] || 'border-gray-200 hover:border-blue-300';
  };

  const StatusFilterButton = ({ label, count, status, onClick, products, breakdown }) => {
    const productBreakdown = products && products.length > 0 
      ? products.map(p => `${p.count}x ${p.model}`).join(', ')
      : null;
    
    const displayText = productBreakdown || breakdown || null;
    
    // Get border color for link cards (cards with onClick)
    const borderColorClass = onClick ? getStatusBorderColor(status) : 'border-gray-200 hover:border-blue-300';

    return (
      <button
        onClick={onClick}
        className={`flex flex-col items-start px-4 py-3 bg-white rounded-lg shadow hover:shadow-md transition-shadow border-2 ${borderColorClass} text-left w-full ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center justify-between w-full mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="flex items-center ml-4">
            <span className="text-lg font-semibold text-gray-900 mr-2">{count}</span>
            {onClick && <ArrowRight className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
        {displayText && (
          <div className="text-xs text-gray-600 mt-1 w-full">
            {displayText}
          </div>
        )}
      </button>
    );
  };

  const SkeletonCard = ({ hasSecondLine = false }) => (
    <div className="flex flex-col items-start px-4 py-3 bg-white rounded-lg shadow border border-gray-200 w-full">
      <div className="flex items-center justify-between w-full mb-1">
        <div className="h-4 bg-gray-300 rounded animate-pulse w-32"></div>
        <div className="flex items-center ml-4">
          <div className="h-6 bg-gray-300 rounded animate-pulse w-8 mr-2"></div>
          <div className="h-4 w-4 bg-gray-300 rounded animate-pulse"></div>
        </div>
      </div>
      {hasSecondLine && (
        <div className="h-3 bg-gray-300 rounded animate-pulse w-full mt-1"></div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

        {/* Orders Section Skeleton */}
        <div className="mb-8">
          <div className="h-6 bg-gray-300 rounded animate-pulse w-24 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonCard hasSecondLine={true} />
            <SkeletonCard hasSecondLine={true} />
          </div>
        </div>

        {/* Repairs Section Skeleton */}
        <div className="mb-8">
          <div className="h-6 bg-gray-300 rounded animate-pulse w-24 mb-4"></div>
          
          {/* Status Filter Links Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <SkeletonCard hasSecondLine={true} />
            <SkeletonCard hasSecondLine={true} />
            <SkeletonCard hasSecondLine={true} />
            <SkeletonCard hasSecondLine={true} />
          </div>

          {/* Repair Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>

        {/* Devices Section Skeleton */}
        <div className="mb-8">
          <div className="h-6 bg-gray-300 rounded animate-pulse w-24 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonCard hasSecondLine={true} />
            <SkeletonCard hasSecondLine={true} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={fetchStats}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const blockedLostRetiredTotal = stats.devices.blocked_last_7_days + 
                                  stats.devices.lost_last_7_days + 
                                  stats.devices.retired_last_7_days;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Orders Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Orders</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatusFilterButton
            label="Waiting for S/Ns"
            count={stats.orders.waiting_for_sns}
            status="pending"
            onClick={() => navigate('/orders?filter=pending')}
            products={stats.orders.waiting_for_sns_products}
          />
          <StatusFilterButton
            label="Orders Completed (Last 7 Days)"
            count={stats.orders.completed_last_7_days}
            products={stats.orders.completed_last_7_days_products}
          />
        </div>
      </div>

      {/* Repairs Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Repairs</h2>
        
        {/* Status Filter Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <StatusFilterButton
            label="Waiting for repair"
            count={stats.repairs.waiting_for_repair}
            status="waiting_for_repair"
            onClick={() => navigate('/repairs?status=waiting_for_repair')}
            products={stats.repairs.waiting_for_repair_products}
          />
          <StatusFilterButton
            label="Work in progress"
            count={stats.repairs.work_in_progress}
            status="work_in_progress"
            onClick={() => navigate('/repairs?status=work_in_progress')}
            products={stats.repairs.work_in_progress_products}
          />
          <StatusFilterButton
            label="Repaired"
            count={stats.repairs.repaired}
            status="repaired"
            onClick={() => navigate('/repairs?status=repaired')}
            products={stats.repairs.repaired_products}
          />
          <StatusFilterButton
            label="Ready for shipping"
            count={stats.repairs.ready_for_shipping}
            status="ready_for_shipping"
            onClick={() => navigate('/repairs?status=ready_for_shipping')}
            products={stats.repairs.ready_for_shipping_products}
          />
        </div>

        {/* Repair Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatusFilterButton
            label="Repairs Created (Last 7 Days)"
            count={stats.repairs.created_last_7_days}
          />
          <StatusFilterButton
            label="Repairs Completed (Last 7 Days)"
            count={stats.repairs.completed_last_7_days}
          />
        </div>
      </div>

      {/* Devices Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Devices</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatusFilterButton
            label="Devices Created (Last 7 Days)"
            count={stats.devices.created_last_7_days}
            products={stats.devices.created_last_7_days_products}
          />
          <StatusFilterButton
            label="Devices Blocked/Lost/Retired (Last 7 Days)"
            count={blockedLostRetiredTotal}
            breakdown={blockedLostRetiredTotal > 0 
              ? `Breakdown: ${stats.devices.blocked_last_7_days} blocked, ${stats.devices.lost_last_7_days} lost, ${stats.devices.retired_last_7_days} retired`
              : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
